import { Button, Dialog } from '@web_opxp/components';
import { FirebaseSelectors, logger, RemoteConfigService, useTranslation } from '@web_opxp/features';
import { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { DEFAULT_MACOS_WARNING } from '../../base/constants';
import ENV from '../../base/env';
import { IState } from '../../store/state';
import { UnitySelectors } from '../../unity';

export interface IMacOsWarningProps {
  enabled: boolean;
}

const agent = ((navigator && navigator.userAgent) || '').toLowerCase();

export function extractSafariVersion(agent: string) {
  const safari = /version\/([\d.]+).+?safari/i.exec(agent);
  return safari ? safari[1] : null;
}

export function isBrowserApplicable(versions = '15.1') {
  // we decided to only check for safari browser and warn user in this case
  // BW-6627 - now we need to detect only 15.1 version
  // BW-6699 - now we need to detect versions defined in the remote config
  const safariVersion = extractSafariVersion(agent);
  if (safariVersion) {
    logger(`Safari version: ${safariVersion}`);
  }
  const enabledVersions = versions.split(',');
  return !!safariVersion && enabledVersions.includes(safariVersion);
}

export const mapStateToProps = (state: IState) => {
  let enabled = false;
  const unityLoading = !UnitySelectors.isUnityStarted(state);
  if (!unityLoading) {
    const firebaseReady = FirebaseSelectors.isReady(state);
    if (firebaseReady) {
      // check remote config and user agent only when unity finished loading
      const macOsWarningConfig = RemoteConfigService.getObjectConfigValue(
        ENV.RemoteConfigMacOsWarning,
        DEFAULT_MACOS_WARNING
      );
      const enabledInConfig = macOsWarningConfig?.enabled;
      if (enabledInConfig) {
        const versions = macOsWarningConfig.versions;
        enabled = isBrowserApplicable(versions);
      }
    }
  }
  return {
    enabled,
  };
};

export function MacOsWarningFC({ enabled }: Readonly<IMacOsWarningProps>) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [wasShown, setWasShown] = useState(false);
  useEffect(() => {
    if (!wasShown) {
      const valueFromStorage = !!localStorage.getItem('macos-warning');
      if (valueFromStorage !== wasShown) {
        setWasShown(valueFromStorage);
      }
    }
    setIsOpen(enabled && !wasShown);
  }, [enabled, wasShown]);
  const handleDialogClose = () => {
    setIsOpen(false);
    setWasShown(true);
    localStorage.setItem('macos-warning', 'true');
  };
  return (
    <Dialog
      autoFocus={true}
      isOpen={isOpen}
      title={t('MacOsWarning.Title')}
      usePortal={true}
      onClose={handleDialogClose}
      footerContent={<Button label={t('MacOsWarning.CTA')} onClick={handleDialogClose} />}
      bodyClassName="legals-dialog-body">
      <p>{t('MacOsWarning.Description')}</p>
    </Dialog>
  );
}

const MacOsWarning = connect(mapStateToProps)(MacOsWarningFC);
export default MacOsWarning;
